import { memo, useCallback, useEffect, useRef, useState } from 'react';
import LottieView from 'lottie-react-native';

import { Platform, StyleProp, StyleSheet, ViewStyle } from 'react-native';
import { LottiePresentConfig } from './LottieProvider.types';
import animations from './animations';
import useBreakpoint from '../../hooks/useBreakpoint';

export let Lottie = {
  present: (_: LottiePresentConfig) => {},
  preload: (_: LottiePresentConfig['animation']) => {},
};

const style: StyleProp<ViewStyle> = [
  StyleSheet.absoluteFill,
  { pointerEvents: 'none', zIndex: 99 },
];

const webStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  zIndex: 999,
  pointerEvents: 'none',
} as const;

function LottieProvider() {
  const breakpoint = useBreakpoint();
  const [config, setConfig] = useState<LottiePresentConfig>();
  const ref = useRef<LottieView>(null);

  const onAnimationFinish = useCallback(
    (isCancelled: boolean) => {
      ref.current?.reset();

      config?.onAnimationFinish?.(isCancelled);

      setConfig(undefined);
    },
    [config],
  );

  useEffect(() => {
    Lottie = {
      present: async (config: LottiePresentConfig) => {
        let source = config.source;

        if (config?.animation && animations[config?.animation]) {
          const animation = animations[config?.animation];

          if (typeof animation === 'function') {
            source = await animation();
          } else if (animation[breakpoint]) {
            source = await animation[breakpoint]();
          }
        }

        config.source = source;

        setConfig(config);
      },
      preload: async (animation: LottiePresentConfig['animation']) => {
        const anim = animations[animation];

        if (typeof anim === 'function') {
          await anim();
        } else if (anim[breakpoint]) {
          await anim[breakpoint]();
        }
      },
    };
  }, [breakpoint]);

  useEffect(() => {
    if (Platform.OS !== 'web' && config?.source) {
      // TODO: There is a bug in the player and autoplay is broken after the first use
      setTimeout(() => {
        ref.current?.play();
      }, 0);
    }
  }, [config]);

  if (!config) {
    return null;
  }

  return (
    <LottieView
      ref={ref}
      style={style}
      webStyle={webStyle}
      loop={false}
      autoPlay={Platform.OS === 'web'}
      {...config}
      onAnimationFinish={onAnimationFinish}
    />
  );
}

export default memo(LottieProvider);
